import { Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ResearchPerfEvalHeader } from '@/module/ResearchPerfEval/components/ResearchPerfEvalHeader';
import { getResearchPerfEvalOptions } from '@/module/ResearchPerfEval/model/ResearchPerfEvalQuery';
import { AuthorResearchPerfEvalHeader } from '@/module/ResearchPerfEval/perRole/AuthorResearchPerfEvalHeader';
import { DepartmentAdminResearchPerfEvalHeader } from '@/module/ResearchPerfEval/perRole/DepartmentAdminResearchPerfEvalHeader';
import { DisciplineAdminResearchPerfEvalHeader } from '@/module/ResearchPerfEval/perRole/DisciplineAdminResearchPerfEvalHeader';
import { FacultyAdminResearchPerfEvalHeader } from '@/module/ResearchPerfEval/perRole/FacultyAdminResearchPerfEvalHeader';
import { UniversityAdminResearchPerfEvalHeader } from '@/module/ResearchPerfEval/perRole/UniversityAdminResearchPerfEvalHeader';

import { NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';

const researchPerfEvalSearchSchema = z.object({
  userId: z.coerce.number().nullish(),
  disciplineId: z.coerce.number().nullish(),
  departmentId: z.coerce.number().nullish(),
  facultyId: z.coerce.number().nullish(),
});

export const Route = createFileRoute('/_auth/research-perf-eval/_layout/$evaluationId/_layout')({
  validateSearch: researchPerfEvalSearchSchema,
  loader: async ({ params: { evaluationId }, context: { queryClient } }) => {
    const evaluation = await queryClient.ensureQueryData(getResearchPerfEvalOptions(evaluationId));

    return {
      evaluation,
      crumb: evaluation.title,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { evaluationId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getResearchPerfEvalOptions(evaluationId));

  const { currentRole } = useCurrentUser();
  const hasAccess =
    ['AUTHOR', 'UNIVERSITY_ADMIN', 'DISCIPLINE_ADMIN', 'DEPARTMENT_ADMIN', 'FACULTY_ADMIN'].indexOf(currentRole) !== -1;

  if (!hasAccess) {
    return (
      <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de autor sau șef de disciplină / director departament sau decan" />
    );
  }

  return (
    <Stack>
      <ResearchPerfEvalHeader evaluation={evaluation} />
      {currentRole === 'UNIVERSITY_ADMIN' && <UniversityAdminResearchPerfEvalHeader />}
      {currentRole === 'FACULTY_ADMIN' && <FacultyAdminResearchPerfEvalHeader />}
      {currentRole === 'DEPARTMENT_ADMIN' && <DepartmentAdminResearchPerfEvalHeader />}
      {currentRole === 'DISCIPLINE_ADMIN' && <DisciplineAdminResearchPerfEvalHeader />}
      {currentRole === 'AUTHOR' && <AuthorResearchPerfEvalHeader />}
    </Stack>
  );
}
